import { useRef } from "react";
import type { JSX } from "react";
import { Link, NavLink, Outlet } from "react-router";
import layout from "../../app/AppLayout.module.css";
import { useKeepFocusInView } from "../../app/useKeepFocusInView";
import { UpdateBanner } from "../../pwa/UpdateBanner";
import { useAuth } from "../../shared/auth/AuthContext";
import { useI18n } from "../../shared/i18n/I18nContext";
import { useMenuSignOut } from "../../shared/session/signOutRequest";
import { AccountMenu } from "../../shared/ui/AccountMenu";
import { AnnouncementOutlet } from "../../shared/ui/Announcer";
import { brandMarkPath } from "../../shared/ui/BrandHeader";
import { cx } from "../../shared/ui/classNames";
import { Icon } from "../../shared/ui/Icon";
import { usePlatformAccess } from "./PlatformAccessContext";
import { platformBarSize, platformPaths, visiblePlatformNavigation } from "./platformNavigation";
import { PlatformUnsentNotice } from "./PlatformUnsentNotice";

/**
 * Shell of the operator area (F-69): the app bar and the navigation look like those of the
 * application of the tenants, but the navigation lists the pages of the area only, the status area
 * shows only the update hint and the number of entries this device has not sent (F-84; no
 * synchronization, no clock, no open days), and the account menu at the name (F-74, the same as in
 * the application of the tenants) leads to the account page of the area and signs out like the menu
 * there (`useMenuSignOut`). The navigation entry of the account page is shown on phones only, as „Mehr“
 * is in the application of the tenants. The navigation has its own landmark name, so assistive
 * technology tells the two areas apart.
 *
 * @returns The layout element.
 */
export function PlatformLayout(): JSX.Element {
  const { t } = useI18n();
  const { user } = useAuth();
  const access = usePlatformAccess();
  const visible = visiblePlatformNavigation(access === "loading" ? "none" : access);
  // Unsent entries or a failed sign-out lead to the account page, which asks or shows the error (F-84).
  const signOutFromMenu = useMenuSignOut(platformPaths.account);
  const mainRef = useRef<HTMLElement>(null);
  useKeepFocusInView(mainRef);

  const shell = (
    <div className={layout["shell"]}>
      <a className={layout["skipLink"]} href="#content">
        {t("app.skipToContent")}
      </a>
      <header className={layout["header"]}>
        <Link className={layout["brand"]} to={platformPaths.root}>
          <img className={layout["logo"]} src={brandMarkPath} alt="" width="28" height="28" />
          <span className={layout["brandName"]}>{t("platform.brand")}</span>
        </Link>
        {user?.displayName == null ? null : (
          <AccountMenu
            className={layout["account"]}
            name={user.displayName}
            label={t("account.menu", { name: user.displayName })}
            caption={t("auth.signedInAs", { name: user.displayName })}
            items={[
              {
                key: "account",
                label: t("platform.nav.account"),
                icon: "account",
                to: platformPaths.account,
              },
              {
                key: "signOut",
                label: t("auth.logout"),
                icon: "signOut",
                onSelect: signOutFromMenu,
              },
            ]}
          />
        )}
      </header>
      <div className={layout["status"]}>
        <UpdateBanner />
        <PlatformUnsentNotice />
      </div>
      <nav className={layout["nav"]} aria-label={t("platform.nav.main")}>
        <ul className={layout["navList"]}>
          {visible.map((item, index) => (
            // Phones show the first entries in the bottom bar; the overview links the others (F-70).
            <li
              key={item.path}
              className={index < platformBarSize ? undefined : layout["overflowItem"]}
            >
              <NavLink
                className={cx(layout["navLink"])}
                to={item.path}
                end={item.path === platformPaths.root}
              >
                <Icon name={item.icon} className={layout["navIcon"]} />
                <span className={layout["navLabel"]}>{t(item.labelKey)}</span>
              </NavLink>
            </li>
          ))}
          <li className={layout["phoneOnlyItem"]}>
            <NavLink className={cx(layout["navLink"])} to={platformPaths.account}>
              <Icon name="account" className={layout["navIcon"]} />
              <span className={layout["navLabel"]}>{t("platform.nav.account")}</span>
            </NavLink>
          </li>
        </ul>
      </nav>
      <main ref={mainRef} id="content" className={layout["main"]} tabIndex={-1}>
        <AnnouncementOutlet />
        <Outlet />
      </main>
    </div>
  );

  return shell;
}
